import type { ChartsAxisContentProps } from "@mui/x-charts/ChartsTooltip";
import type { ScoutDashboardMetricsQuery } from "generated-graphql/graphql";

import { Box, Button, Paper, Skeleton, Stack, Typography, useTheme } from "@mui/material";
import { blueberryTwilightPalette, ChartsReferenceLine } from "@mui/x-charts";
import { LineChart } from "@mui/x-charts/LineChart";
import ChartTooltip from "components/Dashboard/ChartTooltip";
import { useMemo, useState } from "react";
import { prettyPrintDate } from "util/dates";

import { formatChartYAxisValue, formatMetricValue } from "./scoutDashboardFormatters";
import { ScoutMetricDocumentationHeader } from "./ScoutDashboardMetricDocumentation";

type ScoutDashboardWidget =
  ScoutDashboardMetricsQuery["scoutDashboardMetrics"]["chartSections"][number]["widgets"][number];
type ScoutDashboardSeries = ScoutDashboardWidget["series"][number];
type ChartLabelLocation = "tooltip" | "legend";
type ScoutDashboardTooltipChartSeries = {
  id: string | number;
  getColor: (dataIndex: number) => string;
};
type ScoutDashboardTooltipRow = {
  color: string;
  label: string;
  value: string;
};

const DAY_MS = 24 * 60 * 60 * 1000;
const CHART_BASE_HEIGHT = 300;
const CHART_BASE_BOTTOM_MARGIN = 95;
const CHART_LEFT_MARGIN = 95;
const CHART_X_AXIS_LABEL_OFFSET_PX = 54;
const CHART_Y_AXIS_LABEL_OFFSET_PX = -52;
const MAX_VISIBLE_LEGEND_ITEMS = 8;
const NODE_LABEL_WIDGET_IDS = new Set([
  "conversations_per_scout_node",
  "conversations_per_api_node",
  "connected_clients_per_api_node",
  "scout_node_cpu_usage",
  "api_node_cpu_usage",
  "scout_node_memory_usage",
  "api_node_memory_usage",
]);

const timeFormatter = new Intl.DateTimeFormat("en-US", {
  hour: "numeric",
  minute: "2-digit",
});

function truncateChartLabel(label: string): string {
  return label.slice(0, 20);
}

function formatChartTimestamp(value: Date, rangeDurationMs: number): string {
  if (rangeDurationMs <= DAY_MS) {
    return timeFormatter.format(value);
  }
  return prettyPrintDate(value.toISOString());
}

function formatChartValue(value: number | null, unit?: string | null): string {
  if (value === null) {
    return "";
  }

  return formatMetricValue(value, unit);
}

function getChartYAxisMax(widget: ScoutDashboardWidget): number | undefined {
  if (typeof widget.yAxisMax === "number") {
    return widget.yAxisMax;
  }

  if (widget.yAxisMin !== 0) {
    return undefined;
  }

  let maxValue: number | null = null;
  for (const series of widget.series) {
    for (const point of series.points) {
      if (typeof point.value === "number") {
        maxValue = maxValue === null ? point.value : Math.max(maxValue, point.value);
      }
    }
  }

  if (maxValue === 0) {
    return 1;
  }

  return undefined;
}

export function getScoutDashboardLegendSeriesToDisplay<T>(
  seriesToDisplay: T[],
  showAllLegendItems: boolean
): T[] {
  if (showAllLegendItems) {
    return seriesToDisplay;
  }

  return seriesToDisplay.slice(0, MAX_VISIBLE_LEGEND_ITEMS);
}

function getChartLegendLabel(widgetId: string, seriesLabel: string, seriesIndex: number): string {
  const chartLabel = getChartLabel(widgetId, seriesLabel, seriesIndex);
  if (typeof chartLabel === "function") {
    return chartLabel("legend");
  }
  return chartLabel;
}

function getChartLabel(
  widgetId: string,
  seriesLabel: string,
  seriesIndex: number
): string | ((location: ChartLabelLocation) => string) {
  if (!NODE_LABEL_WIDGET_IDS.has(widgetId)) {
    return truncateChartLabel(seriesLabel);
  }

  const nodeLabel = `Node ${seriesIndex + 1}`;
  return (location: ChartLabelLocation) => {
    if (location === "tooltip") {
      return seriesLabel;
    }
    return nodeLabel;
  };
}

function getTooltipColor(
  chartSeries: ScoutDashboardTooltipChartSeries[],
  seriesId: string,
  dataIndex: number
): string {
  const matchingSeries = chartSeries.find((series) => series.id === seriesId);
  return matchingSeries?.getColor(dataIndex) ?? "";
}

function getTooltipLabel(widgetId: string, series: ScoutDashboardSeries) {
  if (NODE_LABEL_WIDGET_IDS.has(widgetId)) {
    return series.label;
  }

  return truncateChartLabel(series.label);
}

export function getScoutDashboardTimeSeriesTooltipRows({
  widget,
  dataIndex,
  chartSeries,
}: {
  widget: Pick<ScoutDashboardWidget, "id" | "series" | "unit">;
  dataIndex: number;
  chartSeries: ScoutDashboardTooltipChartSeries[];
}): ScoutDashboardTooltipRow[] {
  return widget.series
    .map((series) => {
      const point = series.points[dataIndex];
      const value = point?.value;

      if (typeof value !== "number") {
        return undefined;
      }

      return {
        color: getTooltipColor(chartSeries, series.id, dataIndex),
        label: getTooltipLabel(widget.id, series),
        value: formatMetricValue(value, series.unit ?? widget.unit),
      };
    })
    .filter((row): row is ScoutDashboardTooltipRow => row !== undefined);
}

function ScoutDashboardTooltipValue({ label, value }: { label: string; value: string }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="body2" component="span" sx={{ fontWeight: 600 }}>
        {label}
      </Typography>
      <Typography variant="body2" component="span">
        {`: ${value}`}
      </Typography>
    </Box>
  );
}

function getTooltipTitle({
  axisValue,
  widget,
  dataIndex,
  rangeDurationMs,
}: Pick<ChartsAxisContentProps, "axisValue" | "dataIndex"> & {
  widget: ScoutDashboardWidget;
  rangeDurationMs: number;
}): string {
  if (axisValue instanceof Date) {
    return formatChartTimestamp(axisValue, rangeDurationMs);
  }

  const fallbackTimestamp = widget.series[0]?.points[dataIndex ?? -1]?.timestamp;
  const fallbackDate = new Date(fallbackTimestamp ?? "");
  if (Number.isNaN(fallbackDate.getTime())) {
    return "";
  }

  return formatChartTimestamp(fallbackDate, rangeDurationMs);
}

function ScoutDashboardTimeSeriesTooltip({
  widget,
  rangeDurationMs,
  dataIndex,
  axisValue,
  series,
}: Pick<ChartsAxisContentProps, "dataIndex" | "axisValue" | "series"> & {
  widget: ScoutDashboardWidget;
  rangeDurationMs: number;
}) {
  if (dataIndex === undefined || dataIndex === null) {
    return null;
  }

  const title = getTooltipTitle({
    axisValue,
    widget,
    dataIndex,
    rangeDurationMs,
  });
  const rows = getScoutDashboardTimeSeriesTooltipRows({
    widget,
    dataIndex,
    chartSeries: series,
  });

  if (rows.length === 0) {
    return null;
  }

  return (
    <ChartTooltip
      title={title}
      labels={rows.map((row) => ({
        color: row.color,
        value: <ScoutDashboardTooltipValue label={row.label} value={row.value} />,
      }))}
    />
  );
}

function ScoutDashboardTimeSeriesLegend({
  widget,
  colors,
  showAllLegendItems,
}: {
  widget: ScoutDashboardWidget;
  colors: string[];
  showAllLegendItems: boolean;
}) {
  const seriesToDisplay = getScoutDashboardLegendSeriesToDisplay(widget.series, showAllLegendItems);

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "center",
        columnGap: 2,
        rowGap: 1,
        mt: 1,
      }}
    >
      {seriesToDisplay.map((series, index) => (
        <Stack
          key={series.id}
          direction="row"
          alignItems="center"
          spacing={0.5}
          sx={{ minWidth: 0 }}
        >
          <Box
            sx={{
              bgcolor: colors[index % colors.length],
              height: 10,
              width: 10,
              flex: "0 0 auto",
            }}
          />
          <Typography variant="body2" noWrap={true}>
            {getChartLegendLabel(widget.id, series.label, index)}
          </Typography>
        </Stack>
      ))}
    </Box>
  );
}

export function ScoutDashboardTimeSeriesCard({
  widget,
  loading,
  chartRangeKey,
  rangeDurationMs,
}: {
  widget?: ScoutDashboardWidget;
  loading: boolean;
  chartRangeKey?: string;
  rangeDurationMs: number;
  reservedSeriesCount?: number;
}) {
  const theme = useTheme();
  const [showAllLegendItems, setShowAllLegendItems] = useState(false);
  const chartColors = useMemo(
    () => blueberryTwilightPalette(theme.palette.mode),
    [theme.palette.mode]
  );
  const xData = useMemo(
    () => widget?.series[0]?.points.map((point) => new Date(point.timestamp)) ?? [],
    [widget]
  );
  const chartSeries = useMemo(
    () =>
      widget?.series.map((series, index) => ({
        id: series.id,
        data: series.points.map((point) => point.value ?? null),
        color: chartColors[index % chartColors.length],
        label: getChartLabel(widget.id, series.label, index),
        valueFormatter: (value: number | null) => {
          if (typeof value === "number") {
            return formatChartValue(value, series.unit ?? widget.unit);
          }
          return "";
        },
        connectNulls: false,
        showMark: false,
      })) ?? [],
    [chartColors, widget]
  );

  if (loading || !widget) {
    return (
      <Paper variant="outlined" sx={{ p: 2, height: 360 }} data-testid="scout-chart-skeleton">
        <Skeleton width="45%" />
        <Skeleton variant="rectangular" height={280} sx={{ mt: 2 }} />
      </Paper>
    );
  }

  const hasAnyValue = widget.series.some((series) =>
    series.points.some((point) => typeof point.value === "number")
  );
  const hasExpandableLegend = widget.series.length > MAX_VISIBLE_LEGEND_ITEMS;
  const chartHeight = CHART_BASE_HEIGHT;
  const chartBottomMargin = CHART_BASE_BOTTOM_MARGIN;
  const yAxisLabel = widget.unit ?? "Value";
  const yAxisMax = getChartYAxisMax(widget);
  /**
   * Remount charts when the committed dashboard range changes so MUI X Charts never interpolates
   * old line paths against a new time domain. `skipAnimation` below then makes range switches snap
   * directly from the dimmed previous chart to the final matched chart state.
   */
  const chartLifecycleKey = `${widget.id}|${chartRangeKey ?? rangeDurationMs}`;

  return (
    <Paper variant="outlined" sx={{ p: 2, minHeight: 360 }}>
      <Stack spacing={0.5} sx={{ mb: 1 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
          <ScoutMetricDocumentationHeader widget={widget} titleVariant="subtitle1" />
        </Stack>
      </Stack>

      {hasAnyValue ? (
        <LineChart
          key={chartLifecycleKey}
          height={chartHeight}
          skipAnimation={true}
          xAxis={[
            {
              data: xData,
              label: "Time",
              scaleType: "time",
              labelStyle: {
                fill: theme.palette.text.secondary,
                fontSize: 12,
              },
              tickLabelStyle: {
                angle: -45,
                textAnchor: "end",
              },
              valueFormatter: (value) => {
                if (value instanceof Date) {
                  return formatChartTimestamp(value, rangeDurationMs);
                }
                return "";
              },
            },
          ]}
          yAxis={[
            {
              label: yAxisLabel,
              min: widget.yAxisMin ?? undefined,
              max: yAxisMax,
              labelStyle: {
                fill: theme.palette.text.secondary,
                fontSize: 12,
              },
              valueFormatter: (value) => {
                if (typeof value === "number") {
                  return formatChartYAxisValue(value);
                }
                return "";
              },
            },
          ]}
          series={chartSeries}
          colors={chartColors}
          margin={{ top: 20, right: 20, bottom: chartBottomMargin, left: CHART_LEFT_MARGIN }}
          slotProps={{
            legend: {
              hidden: true,
            },
          }}
          slots={{
            axisContent: (props: ChartsAxisContentProps) => (
              <ScoutDashboardTimeSeriesTooltip
                dataIndex={props.dataIndex}
                axisValue={props.axisValue}
                series={props.series}
                widget={widget}
                rangeDurationMs={rangeDurationMs}
              />
            ),
          }}
          sx={{
            "& .MuiChartsAxis-line, & .MuiChartsAxis-tick": {
              stroke: theme.palette.divider,
            },
            "& .MuiChartsAxis-directionX .MuiChartsAxis-label": {
              transform: `translateY(${CHART_X_AXIS_LABEL_OFFSET_PX}px)`,
            },
            "& .MuiChartsAxis-directionY .MuiChartsAxis-label": {
              transform: `translateX(${CHART_Y_AXIS_LABEL_OFFSET_PX}px)`,
            },
          }}
        >
          {widget.referenceLines.map((line) => (
            <ChartsReferenceLine
              key={`${widget.id}-${line.value}-${line.label}`}
              y={line.value}
              label={line.label}
              lineStyle={{ stroke: line.color, strokeDasharray: "5 5", strokeWidth: 2 }}
              labelStyle={{ fill: line.color, fontSize: 11, fontWeight: 600 }}
            />
          ))}
        </LineChart>
      ) : (
        <Stack alignItems="center" justifyContent="center" sx={{ height: chartHeight }}>
          <Typography color="text.secondary">No metric data for this range</Typography>
        </Stack>
      )}
      {hasAnyValue && (
        <ScoutDashboardTimeSeriesLegend
          widget={widget}
          colors={chartColors}
          showAllLegendItems={showAllLegendItems}
        />
      )}
      {hasAnyValue && hasExpandableLegend && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
          <Button
            size="small"
            variant="text"
            onClick={() => setShowAllLegendItems((current) => !current)}
          >
            {showAllLegendItems ? "View less" : "View all"}
          </Button>
        </Box>
      )}
    </Paper>
  );
}
